import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  Tooltip,
  IconButton,
  Alert,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { PoolSettings, PoolSettingsUpdate } from '../../types';

interface NotificationSettingsFormProps {
  settings: PoolSettings['notification_settings'];
  onChange: (data: PoolSettingsUpdate['notification_settings']) => void;
}

const NotificationSettingsForm: React.FC<NotificationSettingsFormProps> = ({
  settings,
  onChange,
}) => {
  const [formData, setFormData] = useState({
    email_alerts: settings.email_alerts,
    sms_alerts: settings.sms_alerts,
    app_alerts: settings.app_alerts,
    webhook_alerts: settings.webhook_alerts,
    alert_frequency: settings.alert_frequency,
  });

  useEffect(() => {
    setFormData({
      email_alerts: settings.email_alerts,
      sms_alerts: settings.sms_alerts,
      app_alerts: settings.app_alerts,
      webhook_alerts: settings.webhook_alerts,
      alert_frequency: settings.alert_frequency,
    });
  }, [settings]);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'immediate' | 'hourly' | 'daily';
    const newFormData = {
      ...formData,
      alert_frequency: value,
    };
    setFormData(newFormData);
    onChange(newFormData);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        알림 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        알림 전송 빈도를 설정. 긴급 알림은 설정과 관계없이 즉시 전송됨.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        이메일·SMS·앱·웹훅 등 알림 채널은 시설 정보의 「현장 안전 알림 채널」에서 설정.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="알림 빈도 설정"
              subheader="알림이 전송되는 주기 설정"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              action={
                <Tooltip title="중요도가 낮은 알림의 빈도를 조절 가능. 긴급 알림은 항상 즉시 전송됨.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <FormControl component="fieldset">
                <RadioGroup
                  name="alert_frequency"
                  value={formData.alert_frequency}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel
                    value="immediate"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>즉시 알림</Typography>
                        <Typography variant="body2" color="text.secondary">
                          모든 알림이 발생 즉시 전송됨
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    value="hourly"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>시간별 요약</Typography>
                        <Typography variant="body2" color="text.secondary">
                          긴급 알림은 즉시, 일반 알림은 시간별로 요약하여 전송.
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    value="daily"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography>일일 요약</Typography>
                        <Typography variant="body2" color="text.secondary">
                          긴급 알림은 즉시, 일반 알림은 일일 요약으로 전송.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                중요도가 높은 알림(위험 수준, 안전 위협)은 설정과 관계없이 항상 즉시 전송.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NotificationSettingsForm;
